import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, Filter } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { /* ignore */ }
  };

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deleted = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (deleted && !deleted.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* ignore */ }
  };

  const clearRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/notifications/clear/read`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (err) { /* ignore */ }
  };

  const handleNotificationClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) navigate(n.link);
  };

  const typeColors = {
    success: { dot: 'bg-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    warning: { dot: 'bg-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
    error: { dot: 'bg-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    info: { dot: 'bg-sky-500', bg: 'bg-sky-500/10', border: 'border-sky-500/20' }
  };

  const categoryIcons = {
    transfer: '💸',
    deposit: '🏦',
    kyc: '📋',
    security: '🔒',
    general: '📬'
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const readCount = notifications.filter(n => n.read).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary flex items-center gap-3">
              <Bell className="h-8 w-8" />
              {t('notif_page_title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('notif_page_subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" className="gap-2 rounded-full" onClick={markAllRead} data-testid="mark-all-read-btn">
                <CheckCheck className="h-4 w-4" />
                {t('notif_mark_all')}
              </Button>
            )}
            {readCount > 0 && (
              <Button variant="outline" size="sm" className="gap-2 rounded-full text-muted-foreground" onClick={clearRead} data-testid="clear-read-btn">
                <Trash2 className="h-4 w-4" />
                {t('notif_clear_read')}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-primary font-mono">{notifications.length}</div>
            <div className="text-xs text-muted-foreground mt-1">{t('notif_total')}</div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-accent font-mono">{unreadCount}</div>
            <div className="text-xs text-muted-foreground mt-1">{t('notif_unread')}</div>
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-emerald-600 font-mono">{readCount}</div>
            <div className="text-xs text-muted-foreground mt-1">{t('notif_read')}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2" data-testid="notification-filters">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {['all', 'unread', 'read'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium smooth-transition ${
                filter === f
                  ? 'bg-accent text-white'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
              data-testid={`filter-${f}`}
            >
              {t(`notif_filter_${f}`)}
            </button>
          ))}
        </div>

        {/* Notification List */}
        <div className="space-y-3" data-testid="notification-list">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
              <Bell className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground text-lg">{filter === 'all' ? t('notif_empty') : t('notif_filter_empty')}</p>
            </div>
          ) : (
            filtered.map((n) => {
              const colors = typeColors[n.notification_type] || typeColors.info;
              return (
                <div
                  key={n.id}
                  className={`group bg-card border rounded-xl p-4 smooth-transition ${
                    !n.read
                      ? `border-accent/30 ${colors.bg}`
                      : 'border-border/50 hover:border-border'
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Category icon */}
                    <div className="text-xl mt-0.5 flex-shrink-0">
                      {categoryIcons[n.category] || categoryIcons.general}
                    </div>

                    {/* Content - clickable */}
                    <div
                      className={`flex-1 min-w-0 ${n.link ? 'cursor-pointer' : ''}`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${colors.dot}`} />
                        <span className={`text-sm font-semibold ${!n.read ? 'text-primary' : 'text-muted-foreground'}`}>
                          {n.title}
                        </span>
                        {!n.read && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent text-white">NEW</span>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${!n.read ? 'text-primary/80' : 'text-muted-foreground'}`}>
                        {n.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[11px] text-muted-foreground">
                          {new Date(n.created_at).toLocaleString()}
                        </span>
                        {n.link && (
                          <span className="text-[11px] text-accent flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            {t('notif_view_detail')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 smooth-transition flex-shrink-0">
                      {!n.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                          className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-primary smooth-transition"
                          title={t('notif_mark_read')}
                          data-testid={`mark-read-${n.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 smooth-transition"
                        title={t('delete')}
                        data-testid={`delete-notif-${n.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
